import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { Close } from "@dreamhub/designsystem/icons";
import Spinner from "@/components/ui/spinner";
import NotFoundPage from "@/components/layouts/NotFoundPage";
import UnsavedChangesWarningDialog from "@/components/layouts/UnsavedChangesWarningDialog";
import { ComputedFields } from "@/services/api/types";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { ContractData } from "@/pages/contracts/types";
import { useContract } from "@/pages/contracts/hooks";
import { useDeals } from "@/pages/deals/hooks";
import { useTermsDraft } from "@/pages/contracts/components/termsDraft/useTermsDraft";
import {
  getDraftRowStatus,
  hasDraftNoteChange
} from "@/pages/contracts/components/termsDraft/types";
import { toApiDate } from "@/pages/contracts/components/termsDraft/apiDates";
import { useTermsVersions } from "@/pages/contracts/components/termsHistory/hooks";
import { findPendingVersion } from "@/pages/contracts/components/termsHistory/pendingVersion";
import UpdateTermsEditor from "./UpdateTermsEditor";
import EffectiveDateSection from "./EffectiveDateSection";
import SaveTermsModal from "./SaveTermsModal";
import CancelAmendmentWarningDialog from "./CancelAmendmentWarningDialog";
import {
  isAmendmentNoLongerPending,
  saveErrorMessageKey
} from "./saveErrorMessage";
import {
  useSaveTermsAmendment,
  useSaveTermsCorrection,
  useTermsFieldSettings
} from "./hooks";
import { toPreviewItems } from "./toPreviewItems";
import { hasShortenedServicePeriod } from "./earlyTermination";
import { useTermsCalculatedValues } from "./useTermsCalculatedValues";
import { TermsUpdateKind, isTermsUpdateKind } from "./types";

export const UPDATE_KIND_SEARCH_PARAM = "kind";

const UpdateTermsPage: React.FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const [searchParams] = useSearchParams();
  const rawKind = searchParams.get(UPDATE_KIND_SEARCH_PARAM) ?? "";
  const updateKind = isTermsUpdateKind(rawKind) ? rawKind : null;

  const {
    data: contract,
    isLoading,
    isError
  } = useContract(contractId, { computed_fields: ComputedFields.ALL });
  const { data: fieldSettings, isLoading: isLoadingSettings } =
    useTermsFieldSettings(contractId);

  // A hand-typed or stale URL must not open an editor the gate would refuse.
  if (isError || !updateKind) {
    return <NotFoundPage data-test="update-terms-404" />;
  }

  if (isLoading || isLoadingSettings || !contract) {
    return (
      <div className="flex items-center justify-center h-full w-full min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!fieldSettings?.canUpdateTerms) {
    return <NotFoundPage data-test="update-terms-forbidden" />;
  }

  return (
    // Keyed on the contract: the route reuses this element across a param change,
    // so without it the draft, effective date and preview figures outlive their contract.
    <UpdateTermsContent
      key={contract.id}
      contract={contract}
      updateKind={updateKind}
      fieldSettings={fieldSettings}
    />
  );
};

interface ContentProps {
  contract: ContractData;
  updateKind: TermsUpdateKind;
  fieldSettings: NonNullable<ReturnType<typeof useTermsFieldSettings>["data"]>;
}

const UpdateTermsContent: React.FC<ContentProps> = ({
  contract,
  updateKind,
  fieldSettings
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const draft = useTermsDraft(contract.revenueItems);
  const queryClient = useQueryClient();
  const saveAmendment = useSaveTermsAmendment(contract.id);
  const saveCorrection = useSaveTermsCorrection(contract.id);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelAmendmentWarning, setShowCancelAmendmentWarning] =
    useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  // Defaults to today, which applies the amendment immediately.
  const [effectiveDate, setEffectiveDate] = useState(() =>
    format(new Date(), "yyyy-MM-dd")
  );
  const isAmendment = updateKind === TermsUpdateKind.AMENDMENT;
  const { enabled: serviceNotesEnabled } = useFeatureFlag(
    FeatureFlags.CRM_10582_SERVICE_NOTES
  );

  // Corrections only: saving one withdraws any scheduled amendment, so the
  // warning needs its effective date. The amendment path is still refused
  // server-side while a version is pending and reports that through
  // `PENDING_VERSION_EXISTS`, so it has nothing to read here. Same query key as
  // the Terms & Services tab, which has usually already filled the cache.
  const {
    data: versionsData,
    isLoading: isLoadingVersions,
    isError: isVersionsError
  } = useTermsVersions(contract.id, { enabled: !isAmendment });
  const pendingAmendment = findPendingVersion(versionsData?.versions);
  // Until the list resolves, "no pending amendment" and "not known yet" look
  // identical, and saving on the second withdraws a scheduled amendment with no
  // warning at all. The tab this route is normally opened from has already
  // filled the cache, but a deep link or a reload lands here cold.
  //
  // A failed read counts as unknown too, and this is the half that is easy to
  // miss: on error TanStack reports `isLoading` false with `data` undefined, so
  // a query that failed is indistinguishable from one that succeeded and found
  // nothing scheduled. Saving through it cancels an amendment the user was
  // never warned about — which is the whole of what this ticket exists to
  // prevent, so availability loses to consent here.
  const isPendingStateUnknown =
    !isAmendment && (isLoadingVersions || isVersionsError);

  // The flag outlives the data it was raised against: the dialog is mounted on
  // `pendingAmendment`, so a refetch replacing that amendment leaves the flag
  // set and the dialog remounts already open, with nobody having asked for it.
  //
  // Keyed on the version id rather than on the amendment disappearing. One
  // refetch can withdraw the scheduled amendment and surface a different one
  // together, so the value goes from one pending version straight to another
  // without ever passing through null — and a guard that only fires on null
  // would let the flag survive exactly that swap. Gating `isOpen` instead
  // cannot work at all: the dialog only renders where the value is already
  // truthy, so the added conjunct is a no-op.
  useEffect(() => {
    setShowCancelAmendmentWarning(false);
  }, [pendingAmendment?.versionId]);

  const { entities: deals } = useDeals(
    contract.dealId ? [contract.dealId] : [],
    { enabled: !!contract.dealId }
  );
  const productId = contract.productId ?? deals[0]?.productId ?? undefined;

  const { values, changeSet, isRefreshing, isStale } = useTermsCalculatedValues(
    contract.id,
    contract,
    draft.items,
    updateKind,
    isAmendment ? toApiDate(effectiveDate) : null,
    serviceNotesEnabled
  );

  const hasPendingChanges = useMemo(
    () =>
      draft.items.length !== contract.revenueItems.length ||
      draft.items.some(
        (item) =>
          getDraftRowStatus(item, draft.baseline) !== null ||
          (serviceNotesEnabled && hasDraftNoteChange(item, draft.baseline))
      ),
    [
      draft.items,
      draft.baseline,
      contract.revenueItems.length,
      serviceNotesEnabled
    ]
  );

  // Every exit returns to the tab the action lives on, as the design shows.
  const contractTermsPath = `/contracts/${contract.id}?tab=termsAndServices`;

  const leaveEditor = () => navigate(contractTermsPath, { replace: true });

  const handleSave = async (amendmentReason?: string) => {
    setIsSaving(true);
    const items = toPreviewItems(
      draft.items,
      serviceNotesEnabled,
      draft.baseline
    );
    try {
      if (isAmendment) {
        await saveAmendment.mutateAsync({
          pathParams: { contractId: contract.id },
          data: {
            items,
            effectiveDate: toApiDate(effectiveDate),
            amendmentReason: amendmentReason?.trim() || null
          }
        });
      } else {
        await saveCorrection.mutateAsync({
          pathParams: { contractId: contract.id },
          data: { items }
        });
      }
      setShowSaveModal(false);
      setShowCancelAmendmentWarning(false);
      toast.success(
        <ToastBody
          title={t(
            isAmendment
              ? "contracts.updateTerms.editor.saveSuccessAmendment"
              : "contracts.updateTerms.editor.saveSuccessCorrection"
          )}
          actions={
            <IconButton
              variant="iconNoBg"
              size="sm"
              onClick={() => toast.dismiss()}
              aria-label={t("close")}
            >
              <Close />
            </IconButton>
          }
        />
      );
      queryClient.invalidateQueries({ queryKey: ["contracts/filter"] });
      // The detail view's financials come from this query and would otherwise read pre-save.
      queryClient.invalidateQueries({
        queryKey: ["contracts/id", { pathParams: { contractId: contract.id } }]
      });
      // A correction withdraws the scheduled amendment and an amendment adds a
      // version, so the timeline and the header badge are both stale either way.
      queryClient.invalidateQueries({
        queryKey: [
          "contracts/terms/versions",
          { pathParams: { contractId: contract.id } }
        ]
      });
      // The per-version detail is a separate key, and View terms reads it. A
      // tab left open on the version this save just withdrew would otherwise
      // keep showing it as due to take effect — the exact thing the withdrawn
      // rendering exists to stop. Prefix match: any version may have changed.
      queryClient.invalidateQueries({
        queryKey: ["contracts/terms/versionDetail"]
      });
      navigate(contractTermsPath, { replace: true });
    } catch (error) {
      // The amendment stopped being pending mid-save, so the warning's own
      // premise no longer holds: close it and refetch rather than leave a
      // dialog naming a date nothing is scheduled for.
      if (isAmendmentNoLongerPending(error)) {
        setShowCancelAmendmentWarning(false);
        queryClient.invalidateQueries({
          queryKey: [
            "contracts/terms/versions",
            { pathParams: { contractId: contract.id } }
          ]
        });
        queryClient.invalidateQueries({
          queryKey: ["contracts/terms/versionDetail"]
        });
      }
      toast.error(t(saveErrorMessageKey(error)));
    } finally {
      setIsSaving(false);
    }
  };

  // The save already captured the pending set, so an edit landing during the
  // request would be discarded by the navigation that follows it.
  const whileNotSaving =
    <Args extends unknown[]>(action: (...args: Args) => void) =>
    (...args: Args) => {
      if (!isSaving) action(...args);
    };

  const handleClose = () => {
    if (isSaving) return;
    if (hasPendingChanges) {
      setShowDiscardConfirm(true);
      return;
    }
    leaveEditor();
  };

  return (
    <>
      <UpdateTermsEditor
        updateKind={updateKind}
        contractName={contract.name}
        bannerClassName={
          updateKind === TermsUpdateKind.CORRECTION
            ? "bg-gold-opacity"
            : "bg-blue-opacity"
        }
        items={draft.items}
        baseline={draft.baseline}
        productId={productId}
        currency={contract.currency}
        calculatedValues={values}
        isCalculating={isRefreshing}
        isCalculationStale={isStale}
        fieldSettings={fieldSettings}
        effectiveDate={isAmendment ? effectiveDate : undefined}
        changeSet={changeSet}
        onAddItem={whileNotSaving(draft.addItem)}
        onUpdateItem={whileNotSaving(draft.updateItem)}
        onDeleteItem={whileNotSaving((id: string) => draft.deleteItems([id]))}
        onRestoreItem={whileNotSaving(draft.restoreItem)}
        onRevertItem={whileNotSaving(draft.revertItem)}
        onClose={handleClose}
        footerActions={
          <Button
            disabled={!hasPendingChanges || isSaving || isPendingStateUnknown}
            // Without this a slow or hung version-list read leaves Save
            // disabled with nothing on screen saying why.
            tooltipText={
              isPendingStateUnknown
                ? t(
                    isVersionsError
                      ? "contracts.updateTerms.editor.scheduledCheckFailed"
                      : "contracts.updateTerms.editor.checkingScheduled"
                  )
                : undefined
            }
            onClick={() => {
              if (isAmendment) {
                setShowSaveModal(true);
              } else if (pendingAmendment) {
                setShowCancelAmendmentWarning(true);
              } else {
                handleSave();
              }
            }}
            data-test="update-terms-editor-save"
          >
            {t(
              isAmendment
                ? "contracts.updateTerms.editor.saveAmendment"
                : "contracts.updateTerms.editor.saveCorrection"
            )}
          </Button>
        }
        headerSection={
          isAmendment ? (
            <EffectiveDateSection
              value={effectiveDate}
              onChange={setEffectiveDate}
            />
          ) : null
        }
      />
      <SaveTermsModal
        isOpen={showSaveModal}
        isSaving={isSaving}
        shortensService={hasShortenedServicePeriod(draft.items, draft.baseline)}
        onKeepEditing={whileNotSaving(() => setShowSaveModal(false))}
        onSave={handleSave}
      />
      {pendingAmendment ? (
        <CancelAmendmentWarningDialog
          isOpen={showCancelAmendmentWarning}
          isSaving={isSaving}
          // Non-null: findPendingVersion rejects a pending row without a date.
          effectiveDate={pendingAmendment.effectiveDate!}
          onKeepEditing={whileNotSaving(() =>
            setShowCancelAmendmentWarning(false)
          )}
          onConfirm={() => handleSave()}
        />
      ) : null}
      <UnsavedChangesWarningDialog
        isOpen={showDiscardConfirm}
        title={t("contracts.updateTerms.discard.title")}
        message={t("contracts.updateTerms.discard.message")}
        cancelButtonText={t("contracts.updateTerms.discard.continue")}
        discardButtonText={t("contracts.updateTerms.discard.discard")}
        onCancel={() => setShowDiscardConfirm(false)}
        onDiscard={leaveEditor}
      />
    </>
  );
};

export default UpdateTermsPage;
